import { useCallback, useMemo, useState } from "react";
import { UIAlternativeFaresReason, UIFlightData } from "@flights/types";
import { useParams } from "react-router-dom";
import useAlternativeFaresParams from "./useAlternativeFaresParams";
import { useClientFetch, useClientFetchCallback } from "./useClientFetch";
import { clientFetch } from "@flights/client-fetch";
import { ALTERNATIVE_FARES_RESULTS, REFRESH_RESULTS_TIMEOUT, SEATMAP_EXTRA_TYPE } from "../constants";
import { flightsLocalStore, getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import useSearchURL from "./useSearchURL";

const useMakeAlternativeFaresUrl = () => {
  const url = useSearchURL();
  const { getAlternativeFaresParams } = useAlternativeFaresParams();
  const searchUrl = url?.searchresults || window.location.href;
  const alternativeFaresParams = getAlternativeFaresParams();

  const { flightToken } = useParams<{ flightToken: string }>();

  return useMemo(() => {
    const url = new URL(`https://x/${searchUrl}`);
    const flightDetailsParams = new URLSearchParams(url.search);

    if (alternativeFaresParams?.excludedAncillaries) {
      flightDetailsParams.set("excludedAncillaries", alternativeFaresParams.excludedAncillaries);
    }

    if (alternativeFaresParams?.salesCountry) {
      flightDetailsParams.set("salesCountry", alternativeFaresParams.salesCountry);
    }

    if (flightToken) {
      flightDetailsParams.set("offerToken", flightToken);
    }

    if (alternativeFaresParams?.searchedAt) {
      flightDetailsParams.set("searchedAt", new Date(alternativeFaresParams.searchedAt).toISOString());
    }

    const externalOrigin = getMarketingTrackingVariable("ext-origin");
    const externalForward = getMarketingTrackingVariable("ext-fwd");
    const salesCountry = getMarketingTrackingVariable("salesCountry");

    if (externalOrigin) {
      flightDetailsParams.set("salesChannel", externalOrigin);
    }
    if (externalForward) {
      flightDetailsParams.set("salesIdentifier", externalForward);
    }
    if (salesCountry) {
      flightDetailsParams.set("salesCountry", salesCountry);
    }

    // Excluding the seatmap from the subsequent downstream calls to offer service. We don't need the seat map
    flightDetailsParams.set("excludedAncillaries", SEATMAP_EXTRA_TYPE);

    //! Missing price in search

    return `/api/offerAlternativeFares?${flightDetailsParams.toString()}`;
  }, [
    alternativeFaresParams?.excludedAncillaries,
    alternativeFaresParams?.salesCountry,
    alternativeFaresParams?.searchedAt,
    flightToken,
    searchUrl
  ]);
};

type FetchAlternativeFaresOptions = {
  enabled?: boolean;
};

type Params = {
  soldOutOffer: Partial<UIFlightData>;
  orderId: string;
  reason: UIAlternativeFaresReason;
  options?: FetchAlternativeFaresOptions;
};

const useFetchAlternativeFares = ({ soldOutOffer, orderId, reason, options }: Params) => {
  const { clientFetchCallback } = useClientFetchCallback();
  const alternativeFaresUrl = useMakeAlternativeFaresUrl();
  const [alternativeFares, setAlternativesFares] = useState<UIFlightData[]>([]);
  const [alternativeFaresLoading, setAlternativeFaresLoading] = useState(true);
  const [alternativeFaresError, setAlternativeFaresError] = useState(false);

  const cachedAlternativeFares: UIFlightData[] | undefined = flightsLocalStore.get(ALTERNATIVE_FARES_RESULTS);

  const shouldFetch = options?.enabled ?? true;

  const fetchOptions = useMemo(
    () => ({
      method: "POST",
      body: JSON.stringify({ flightOffer: soldOutOffer, orderId, reason }),
      headers: {
        "Content-Type": "application/json"
      }
    }),
    [orderId, reason, soldOutOffer]
  );

  const onSuccess = useCallback(
    (data: UIFlightData[]) => {
      flightsLocalStore.set(ALTERNATIVE_FARES_RESULTS, data, { ttl: REFRESH_RESULTS_TIMEOUT });

      void clientFetch("/api/order/track-alternative-fares-event", {
        method: "POST",
        body: JSON.stringify({
          eventType: "ALTERNATIVE_FARE_SHOWED",
          orderId,
          alternativeOffers: data
        }),
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json"
        }
      });

      setAlternativeFaresLoading(false);
      setAlternativesFares(data);
    },
    [orderId]
  );

  const onError = useCallback(() => {
    void clientFetch("/api/order/track-alternative-fares-event", {
      method: "POST",
      body: JSON.stringify({
        eventType: "ALTERNATIVE_FARE_ERROR",
        orderId
      }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json"
      }
    });
    setAlternativeFaresError(true);
    setAlternativeFaresLoading(false);
  }, [orderId]);

  const fetchData = useCallback(() => {
    if (cachedAlternativeFares) return;

    setAlternativeFaresError(false);
    setAlternativeFaresLoading(true);

    clientFetchCallback(alternativeFaresUrl, fetchOptions).then(onSuccess).catch(onError);
  }, [alternativeFaresUrl, cachedAlternativeFares, clientFetchCallback, fetchOptions, onError, onSuccess]);

  useClientFetch(alternativeFaresUrl, {
    ...fetchOptions,
    condition: () => shouldFetch && !cachedAlternativeFares,
    fetch: () => setAlternativeFaresLoading(true),
    success: onSuccess,
    error: onError
  });

  const fares = cachedAlternativeFares ? cachedAlternativeFares : alternativeFares;
  const loading = cachedAlternativeFares ? false : alternativeFaresLoading;
  const error = cachedAlternativeFares ? false : alternativeFaresError;

  return {
    alternativeFares: fares,
    alternativeFaresLoading: loading,
    alternativeFaresError: error,
    refetch: fetchData
  };
};

export default useFetchAlternativeFares;
